import { motion } from 'framer-motion';

type OrbState = 'idle' | 'listening' | 'thinking' | 'speaking';

interface VoiceOrbProps {
  state: OrbState;
  color?: string;
  size?: number;
  label?: string;
}

/** Concentric sound-wave circles. Animates strongly while the agent is speaking. */
export function VoiceOrb({ state, color = '#e60000', size = 160, label }: VoiceOrbProps) {
  const speaking = state === 'speaking';
  const listening = state === 'listening';
  const active = speaking || listening;
  const ringColor = listening ? '#22c55e' : color;

  const rings = [0, 1, 2, 3];

  return (
    <div className="relative flex items-center justify-center" style={{ width: size, height: size }}>
      {/* Expanding sound-wave rings */}
      {rings.map((i) => (
        <motion.span
          key={i}
          className="absolute rounded-full"
          style={{ border: `2px solid ${ringColor}`, width: size * 0.5, height: size * 0.5 }}
          initial={{ opacity: 0, scale: 0.5 }}
          animate={
            active
              ? { opacity: [0.5, 0], scale: [0.5, 1.9] }
              : { opacity: 0.12, scale: 1 }
          }
          transition={
            active
              ? { duration: speaking ? 1.6 : 2.2, repeat: Infinity, delay: i * (speaking ? 0.4 : 0.55), ease: 'easeOut' }
              : { duration: 0.4 }
          }
        />
      ))}

      {/* Equalizer bars (only while speaking) */}
      {speaking && (
        <div className="absolute flex items-end gap-1" style={{ height: size * 0.28 }}>
          {[0, 1, 2, 3, 4, 5, 6].map((i) => (
            <motion.span
              key={i}
              className="w-1.5 rounded-full"
              style={{ background: color }}
              animate={{ height: ['20%', '100%', '35%', '85%', '25%'] }}
              transition={{ duration: 0.7 + (i % 3) * 0.15, repeat: Infinity, ease: 'easeInOut', delay: i * 0.06 }}
            />
          ))}
        </div>
      )}

      {/* Core */}
      <motion.div
        className="rounded-full flex items-center justify-center"
        style={{
          width: size * 0.46,
          height: size * 0.46,
          background: `radial-gradient(circle at 35% 30%, ${ringColor}, ${ringColor}66 70%)`,
          boxShadow: active ? `0 0 40px ${ringColor}aa` : `0 0 14px ${ringColor}44`,
        }}
        animate={active ? { scale: speaking ? [1, 1.08, 1] : [1, 1.04, 1] } : { scale: 1 }}
        transition={{ duration: speaking ? 0.5 : 1.2, repeat: active ? Infinity : 0, ease: 'easeInOut' }}
      >
        {!speaking && label && <span className="text-white text-xs font-medium px-2 text-center">{label}</span>}
      </motion.div>
    </div>
  );
}
